import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { useToast } from "../../../components/toast/useToast";
import SiaService from "./SiaService";
import type {
    SiaAccount,
    SiaAccountDetail,
    CreateSiaAccountRequest,
    UpdateSiaAccountRequest,
    SiaAccountPasswordResponse,
    SiaZone,
    CreateSiaZoneRequest,
    UpdateSiaZoneRequest,
    SiaMuteRequest,
    SiaMuteResponse,
    SiaEvent,
    SiaCodeKonfig,
    RelayKonfig,
    CreateRelayKonfigRequest,
    UpdateRelayKonfigRequest,
    RelayCommandRequest,
    RelayCommandResponse,
} from "../generated";
import { IOT_QUERY_KEYS } from "./IotMutations";

// =====================
// QUERY KEYS
// =====================

export const SIA_QUERY_KEYS = {
    accounts: ["siaAccounts"] as const,
    account: (id: number) => ["siaAccount", id] as const,
    accountsByObjekt: (objektId: number) => ["siaAccountsByObjekt", objektId] as const,
    previewAccountId: (objektId: number) => ["siaPreviewAccountId", objektId] as const,
    zones: ["siaZones"] as const,
    zone: (id: number) => ["siaZone", id] as const,
    zonesByAccount: (accountId: number) => ["siaZonesByAccount", accountId] as const,
    eventsByAccount: (accountId: number) => ["siaEventsByAccount", accountId] as const,
    eventsByZone: (zoneId: number) => ["siaEventsByZone", zoneId] as const,
    codes: ["siaCodes"] as const,
    code: (id: number) => ["siaCode", id] as const,
    relayKonfig: ["relayKonfig"] as const,
    relayKonfigById: (id: number) => ["relayKonfigById", id] as const,
    relayKonfigByObjekt: (objektId: number) => ["relayKonfigByObjekt", objektId] as const,
};

// =====================
// QUERIES - SIA ACCOUNTS
// =====================

export const useSiaAccountsQuery = () => {
    return useQuery<SiaAccount[], Error>({
        queryKey: SIA_QUERY_KEYS.accounts,
        queryFn: () => SiaService.getAllSiaAccounts(),
    });
};

export const useSiaAccountQuery = (id: number) => {
    return useQuery<SiaAccountDetail, Error>({
        queryKey: SIA_QUERY_KEYS.account(id),
        queryFn: () => SiaService.getSiaAccountById(id),
        enabled: !!id,
    });
};

export const useSiaAccountsByObjektQuery = (objektId: number) => {
    return useQuery<SiaAccount[], Error>({
        queryKey: SIA_QUERY_KEYS.accountsByObjekt(objektId),
        queryFn: () => SiaService.getSiaAccountsByObjekt(objektId),
        enabled: !!objektId,
    });
};

export const useSiaPreviewAccountIdQuery = (objektId: number, enabled = true) => {
    return useQuery({
        queryKey: SIA_QUERY_KEYS.previewAccountId(objektId),
        queryFn: () => SiaService.previewAccountId(objektId),
        enabled: !!objektId && enabled,
    });
};

// =====================
// QUERIES - SIA ZONES
// =====================

export const useSiaZonesQuery = () => {
    return useQuery<SiaZone[], Error>({
        queryKey: SIA_QUERY_KEYS.zones,
        queryFn: () => SiaService.getAllSiaZones(),
    });
};

export const useSiaZoneQuery = (id: number) => {
    return useQuery<SiaZone, Error>({
        queryKey: SIA_QUERY_KEYS.zone(id),
        queryFn: () => SiaService.getSiaZoneById(id),
        enabled: !!id,
    });
};

export const useSiaZonesByAccountQuery = (accountId: number) => {
    return useQuery<SiaZone[], Error>({
        queryKey: SIA_QUERY_KEYS.zonesByAccount(accountId),
        queryFn: () => SiaService.getSiaZonesByAccount(accountId),
        enabled: !!accountId,
    });
};

// =====================
// QUERIES - SIA EVENTS
// =====================

export const useSiaEventsByAccountQuery = (accountId: number, limit = 100) => {
    return useQuery<SiaEvent[], Error>({
        queryKey: SIA_QUERY_KEYS.eventsByAccount(accountId),
        queryFn: () => SiaService.getSiaEventsByAccount(accountId, limit),
        enabled: !!accountId,
    });
};

export const useSiaEventsByZoneQuery = (zoneId: number, limit = 100) => {
    return useQuery<SiaEvent[], Error>({
        queryKey: SIA_QUERY_KEYS.eventsByZone(zoneId),
        queryFn: () => SiaService.getSiaEventsByZone(zoneId, limit),
        enabled: !!zoneId,
    });
};

// =====================
// QUERIES - SIA CODES
// =====================

export const useSiaCodesQuery = () => {
    return useQuery<SiaCodeKonfig[], Error>({
        queryKey: SIA_QUERY_KEYS.codes,
        queryFn: () => SiaService.getAllSiaCodes(),
    });
};

export const useSiaCodeQuery = (id: number) => {
    return useQuery<SiaCodeKonfig, Error>({
        queryKey: SIA_QUERY_KEYS.code(id),
        queryFn: () => SiaService.getSiaCodeById(id),
        enabled: !!id,
    });
};

// =====================
// QUERIES - RELAY KONFIG
// =====================

export const useRelayKonfigQuery = () => {
    return useQuery<RelayKonfig[], Error>({
        queryKey: SIA_QUERY_KEYS.relayKonfig,
        queryFn: () => SiaService.getAllRelayKonfig(),
    });
};

export const useRelayKonfigByIdQuery = (id: number) => {
    return useQuery<RelayKonfig, Error>({
        queryKey: SIA_QUERY_KEYS.relayKonfigById(id),
        queryFn: () => SiaService.getRelayKonfigById(id),
        enabled: !!id,
    });
};

export const useRelayKonfigByObjektQuery = (objektId: number) => {
    return useQuery<RelayKonfig[], Error>({
        queryKey: SIA_QUERY_KEYS.relayKonfigByObjekt(objektId),
        queryFn: () => SiaService.getRelayKonfigByObjekt(objektId),
        enabled: !!objektId,
    });
};

// =====================
// MUTATIONS - SIA ACCOUNTS
// =====================

export const useCreateSiaAccountMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<SiaAccount, Error, CreateSiaAccountRequest>({
        mutationFn: (data) => SiaService.createSiaAccount(data),

        onSuccess: (_, { objektId }) => {
            queryClient.invalidateQueries({ queryKey: SIA_QUERY_KEYS.accounts });
            queryClient.invalidateQueries({ queryKey: SIA_QUERY_KEYS.accountsByObjekt(objektId) });
            queryClient.invalidateQueries({ queryKey: IOT_QUERY_KEYS.objekt(objektId) });
            showToast("SIA account byl vytvořen", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se vytvořit SIA account", "error");
        },
    });
};

export const useUpdateSiaAccountMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<SiaAccount, Error, { id: number; data: UpdateSiaAccountRequest }>({
        mutationFn: ({ id, data }) => SiaService.updateSiaAccount(id, data),

        onSuccess: (result, { id }) => {
            queryClient.invalidateQueries({ queryKey: SIA_QUERY_KEYS.accounts });
            queryClient.invalidateQueries({ queryKey: SIA_QUERY_KEYS.account(id) });
            queryClient.invalidateQueries({ queryKey: SIA_QUERY_KEYS.accountsByObjekt(result.objektId) });
            showToast("SIA account byl aktualizován", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se aktualizovat SIA account", "error");
        },
    });
};

export const useDeleteSiaAccountMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<void, Error, { id: number; objektId: number }>({
        mutationFn: ({ id }) => SiaService.deleteSiaAccount(id),

        onSuccess: (_, { id, objektId }) => {
            queryClient.invalidateQueries({ queryKey: SIA_QUERY_KEYS.accounts });
            queryClient.removeQueries({ queryKey: SIA_QUERY_KEYS.account(id) });
            queryClient.invalidateQueries({ queryKey: SIA_QUERY_KEYS.accountsByObjekt(objektId) });
            queryClient.invalidateQueries({ queryKey: IOT_QUERY_KEYS.objekt(objektId) });
            showToast("SIA account byl smazán", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se smazat SIA account", "error");
        },
    });
};

export const useGetSiaPasswordMutation = () => {
    const { showToast } = useToast();

    return useMutation<SiaAccountPasswordResponse, Error, number>({
        mutationFn: (id) => SiaService.getSiaAccountPassword(id),

        onError: (error) => {
            showToast(error.message || "Nepodařilo se získat heslo", "error");
        },
    });
};

export const useRegenerateSiaPasswordMutation = () => {
    const { showToast } = useToast();

    return useMutation<SiaAccountPasswordResponse, Error, number>({
        mutationFn: (id) => SiaService.regenerateSiaAccountPassword(id),

        onSuccess: () => {
            showToast("Nové heslo bylo vygenerováno", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se vygenerovat heslo", "error");
        },
    });
};

// =====================
// MUTATIONS - SIA ZONES
// =====================

export const useCreateSiaZoneMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<SiaZone, Error, CreateSiaZoneRequest>({
        mutationFn: (data) => SiaService.createSiaZone(data),

        onSuccess: (_, { siaAccountId }) => {
            queryClient.invalidateQueries({ queryKey: SIA_QUERY_KEYS.zones });
            queryClient.invalidateQueries({ queryKey: SIA_QUERY_KEYS.zonesByAccount(siaAccountId) });
            queryClient.invalidateQueries({ queryKey: SIA_QUERY_KEYS.account(siaAccountId) });
            showToast("SIA zóna byla vytvořena", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se vytvořit SIA zónu", "error");
        },
    });
};

export const useUpdateSiaZoneMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<SiaZone, Error, { id: number; accountId: number; data: UpdateSiaZoneRequest }>({
        mutationFn: ({ id, data }) => SiaService.updateSiaZone(id, data),

        onSuccess: (_, { id, accountId }) => {
            queryClient.invalidateQueries({ queryKey: SIA_QUERY_KEYS.zones });
            queryClient.invalidateQueries({ queryKey: SIA_QUERY_KEYS.zone(id) });
            queryClient.invalidateQueries({ queryKey: SIA_QUERY_KEYS.zonesByAccount(accountId) });
            queryClient.invalidateQueries({ queryKey: SIA_QUERY_KEYS.account(accountId) });
            showToast("SIA zóna byla aktualizována", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se aktualizovat SIA zónu", "error");
        },
    });
};

export const useDeleteSiaZoneMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<void, Error, { id: number; accountId: number }>({
        mutationFn: ({ id }) => SiaService.deleteSiaZone(id),

        onSuccess: (_, { id, accountId }) => {
            queryClient.invalidateQueries({ queryKey: SIA_QUERY_KEYS.zones });
            queryClient.removeQueries({ queryKey: SIA_QUERY_KEYS.zone(id) });
            queryClient.invalidateQueries({ queryKey: SIA_QUERY_KEYS.zonesByAccount(accountId) });
            queryClient.invalidateQueries({ queryKey: SIA_QUERY_KEYS.account(accountId) });
            showToast("SIA zóna byla smazána", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se smazat SIA zónu", "error");
        },
    });
};

export const useMuteSiaZoneMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<SiaMuteResponse, Error, { id: number; accountId: number; data: SiaMuteRequest }>({
        mutationFn: ({ id, data }) => SiaService.muteSiaZone(id, data),

        onSuccess: (result, { id, accountId }) => {
            queryClient.invalidateQueries({ queryKey: SIA_QUERY_KEYS.zone(id) });
            queryClient.invalidateQueries({ queryKey: SIA_QUERY_KEYS.zonesByAccount(accountId) });
            queryClient.invalidateQueries({ queryKey: SIA_QUERY_KEYS.account(accountId) });
            const muteDo = result.muteDo ? new Date(result.muteDo).toLocaleString('cs-CZ') : '';
            const bypassMsg = result.bypassAktivovan ? ' Bypass aktivován.' : '';
            showToast(`Zóna ztlumena do ${muteDo}.${bypassMsg}`, "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se ztlumit zónu", "error");
        },
    });
};

export const useUnmuteSiaZoneMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<SiaMuteResponse, Error, { id: number; accountId: number }>({
        mutationFn: ({ id }) => SiaService.unmuteSiaZone(id),

        onSuccess: (_, { id, accountId }) => {
            queryClient.invalidateQueries({ queryKey: SIA_QUERY_KEYS.zone(id) });
            queryClient.invalidateQueries({ queryKey: SIA_QUERY_KEYS.zonesByAccount(accountId) });
            queryClient.invalidateQueries({ queryKey: SIA_QUERY_KEYS.account(accountId) });
            showToast("Ztlumení zóny bylo zrušeno", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se zrušit ztlumení", "error");
        },
    });
};

// =====================
// MUTATIONS - SIA CODES
// =====================

export const useUpdateSiaCodeMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<SiaCodeKonfig, Error, { id: number; data: Partial<SiaCodeKonfig> }>({
        mutationFn: ({ id, data }) => SiaService.updateSiaCode(id, data),

        onSuccess: (_, { id }) => {
            queryClient.invalidateQueries({ queryKey: SIA_QUERY_KEYS.codes });
            queryClient.invalidateQueries({ queryKey: SIA_QUERY_KEYS.code(id) });
            showToast("Konfigurace SIA kódu byla aktualizována", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se aktualizovat konfiguraci", "error");
        },
    });
};

// =====================
// MUTATIONS - RELAY KONFIG
// =====================

export const useCreateRelayKonfigMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<RelayKonfig, Error, CreateRelayKonfigRequest>({
        mutationFn: (data) => SiaService.createRelayKonfig(data),

        onSuccess: (_, { objektId }) => {
            queryClient.invalidateQueries({ queryKey: SIA_QUERY_KEYS.relayKonfig });
            queryClient.invalidateQueries({ queryKey: SIA_QUERY_KEYS.relayKonfigByObjekt(objektId) });
            showToast("Relay konfigurace byla vytvořena", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se vytvořit relay konfiguraci", "error");
        },
    });
};

export const useUpdateRelayKonfigMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<RelayKonfig, Error, { id: number; objektId: number; data: UpdateRelayKonfigRequest }>({
        mutationFn: ({ id, data }) => SiaService.updateRelayKonfig(id, data),

        onSuccess: (_, { id, objektId }) => {
            queryClient.invalidateQueries({ queryKey: SIA_QUERY_KEYS.relayKonfig });
            queryClient.invalidateQueries({ queryKey: SIA_QUERY_KEYS.relayKonfigById(id) });
            queryClient.invalidateQueries({ queryKey: SIA_QUERY_KEYS.relayKonfigByObjekt(objektId) });
            showToast("Relay konfigurace byla aktualizována", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se aktualizovat relay konfiguraci", "error");
        },
    });
};

export const useDeleteRelayKonfigMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<void, Error, { id: number; objektId: number }>({
        mutationFn: ({ id }) => SiaService.deleteRelayKonfig(id),

        onSuccess: (_, { id, objektId }) => {
            queryClient.invalidateQueries({ queryKey: SIA_QUERY_KEYS.relayKonfig });
            queryClient.removeQueries({ queryKey: SIA_QUERY_KEYS.relayKonfigById(id) });
            queryClient.invalidateQueries({ queryKey: SIA_QUERY_KEYS.relayKonfigByObjekt(objektId) });
            showToast("Relay konfigurace byla smazána", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se smazat relay konfiguraci", "error");
        },
    });
};

export const useSendRelayCommandMutation = () => {
    const { showToast } = useToast();

    return useMutation<RelayCommandResponse, Error, { objektId: number; relayZoneId: number; data: RelayCommandRequest }>({
        mutationFn: ({ objektId, relayZoneId, data }) =>
            SiaService.sendRelayCommand(objektId, relayZoneId, data),

        onSuccess: (result) => {
            const commandName = result.command === 'ON' ? 'Zapnuto' :
                result.command === 'OFF' ? 'Vypnuto' : 'Puls odeslán';
            showToast(`Relay ${result.relayZoneId}: ${commandName}`, "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se odeslat relay příkaz", "error");
        },
    });
};
